import React, { useState, useEffect } from 'react';
import { Check, X, Edit3, Clock } from 'lucide-react';
import { useDistributors } from '../../../../../app/modules/auth'
import Select from 'react-select';

interface EditableCellProps {
  value: string;
  type: 'data' | 'número' | 'texto' | 'categoria_mercado' | 'yesno' | 'status_prospeccao' | 'status_industria' | 'percent' | 'distributors' | 'distributors_count';
  onSave: (newValue: string) => Promise<boolean>;
  disabled?: boolean;
  onShowHistory?: () => void;
}

export const EditableCell: React.FC<EditableCellProps> = ({
  value,
  type,
  onSave,
  disabled = false,
  onShowHistory,
}) => {
  const distributorsData = useDistributors()
  const distributors:any = distributorsData.map((dist: any) => ({
      value: dist.value,
      label: dist.label,
  }));

  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  // Funções movidas para o topo para evitar erro de inicialização
  const formatPercentValue = (val: string) => {
    if (!val || val === '') return '';
    const numValue = parseFloat(val);
    if (isNaN(numValue)) return val;
    return numValue.toFixed(2) + '%';
  };

  const getValueDisplay = () => {
    if (!value) {
      return <span className="text-gray-400 italic">Vazio</span>;
    }

    // Format date display
    if (type === 'data') {
      try {
        // Parse date as local date to avoid timezone issues
        const [year, month, day] = value.split('-').map(Number);
        const date = new Date(year, month - 1, day); // month is 0-indexed
        return date.toLocaleDateString('pt-BR');
      } catch {
        return value;
      }
    }

    // Format percent display
    if (type === 'percent') {
      return <span className="font-medium text-blue-600">{formatPercentValue(value)}</span>;
    }

    // Add visual indicators for status fields
    if (type === 'yesno') {
      const color = value === 'Sim' ? 'text-green-600' : value === 'Não' ? 'text-red-600' : 'text-gray-600';
      const icon = value === 'Sim' ? '✅' : value === 'Não' ? '❌' : '⚪';
      return (
        <span className={`${color} flex items-center space-x-1`}>
          <span>{icon}</span>
          <span>{value}</span>
        </span>
      );
    }

    if (type === 'status_prospeccao') {
      const getStatusColor = () => {
        switch (value) {
          case 'Sem impeditivos': return 'text-green-600 bg-green-50 border-green-200';
          case 'Travas jurídicas': return 'text-red-600 bg-red-50 border-red-200';
          case 'Não possuem sellers compatíveis': return 'text-orange-600 bg-orange-50 border-orange-200';
          case 'Sem retorno da indústria': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
          default: return 'text-gray-600 bg-gray-50 border-gray-200';
        }
      };
      
      return (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor()}`}>
          {value}
        </span>
      );
    }

    if (type === 'status_industria') {
      const getStatusColor = () => {
        switch (value) {
          case 'Parceiras': return 'text-green-600 bg-green-50 border-green-200';
          case 'Em setup': return 'text-blue-600 bg-blue-50 border-blue-200';
          case 'Em negociação': return 'text-purple-600 bg-purple-50 border-purple-200';
          case 'Em prospecção': return 'text-orange-600 bg-orange-50 border-orange-200';
          case 'Inativo': return 'text-gray-600 bg-gray-50 border-gray-200';
          default: return 'text-gray-600 bg-gray-50 border-gray-200';
        }
      };
      
      return (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor()}`}>
          {value}
        </span>
      );
    }

    if (type === 'categoria_mercado') {
      const getCategoryColor = () => {
        switch (value) {
          case 'Alimentos': return 'text-green-600 bg-green-50 border-green-200';
          case 'Bebidas': return 'text-blue-600 bg-blue-50 border-blue-200';
          case 'Higiene & Beleza': return 'text-pink-600 bg-pink-50 border-pink-200';
          case 'Limpeza': return 'text-cyan-600 bg-cyan-50 border-cyan-200';
          case 'Outros': return 'text-gray-600 bg-gray-50 border-gray-200';
          default: return 'text-gray-600 bg-gray-50 border-gray-200';
        }
      };
      
      return (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getCategoryColor()}`}>
          {value}
        </span>
      );
    }

      if (type === 'distributors') {
        const ids = value.split(',').filter(Boolean);
        const labels = ids
          .map(id => distributors.find((d:any) => String(d.value) === String(id))?.label || id)
          .join(', ');
        return <span className="text-gray-800">{labels || <i className="text-gray-400">Vazio</i>}</span>;
      }
      
      if (type === 'distributors_count') {
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
            {value}
          </span>
        );
      }

    return value;
  };

  const handleSave = async () => {
    if (editValue === value) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    const success = await onSave(editValue);
    
    if (success) {
      setIsEditing(false);
    } else {
      setEditValue(value); // Revert on failure
    }
    
    setIsSaving(false);
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const getInputType = () => {
    switch (type) {
      case 'data':
        return 'date';
      case 'número':
      case 'percent':
        return 'number';
      case 'categoria_mercado':
      case 'yesno':
      case 'status_prospeccao':
      case 'status_industria':
      case 'distributors':
        return 'select';
      case 'distributors_count':
        return 'number';
      default:
        return 'text';
    }
  };

  const getSelectOptions = () => {
    switch (type) {
      case 'categoria_mercado':
        return [
          { value: '', label: 'Vazio' },
          { value: 'Laticínios', label: 'Laticínios' },
          { value: 'Alimentos', label: 'Alimentos' },
          { value: 'Automotivo', label: 'Automotivo' },
          { value: 'Bazar', label: 'Bazar' },
          { value: 'Bebidas', label: 'Bebidas' },
          { value: 'Brinquedos', label: 'Brinquedos' },
          { value: 'Calçados', label: 'Calçados' },
          { value: 'Cigarros', label: 'Cigarros' },
          { value: 'Cuidados Pessoais', label: 'Cuidados Pessoais' },
          { value: 'Doces e Guloseimas', label: 'Doces e Guloseimas' },
          { value: 'Ecommerce', label: 'Ecommerce' },
          { value: 'Eletrodomésticos', label: 'Eletrodomésticos' },
          { value: 'Gás', label: 'Gás' },
          { value: 'Higiene', label: 'Higiene' },
          { value: 'Limpeza', label: 'Limpeza' },
          { value: 'Pet care', label: 'Pet care' },
          { value: 'Saúde', label: 'Saúde' },
          { value: 'Utilidades domésticas', label: 'Utilidades domésticas' },
          { value: 'Vestuário', label: 'Vestuário' },
          { value: 'Outros', label: 'Outros' }
        ];
      case 'yesno':
        return [
          { value: '', label: 'Vazio' },
          { value: 'Sim', label: 'Sim' },
          { value: 'Não', label: 'Não' },
        ];
      case 'status_prospeccao':
        return [
          { value: '', label: 'Vazio' },
          { value: 'Travas jurídicas', label: 'Travas jurídicas' },
          { value: 'Não possuem sellers compatíveis', label: 'Não possuem sellers compatíveis' },
          { value: 'Sem retorno da indústria', label: 'Sem retorno da indústria' },
          { value: 'Sem impeditivos', label: 'Sem impeditivos' },
        ];
      case 'status_industria':
        return [
          { value: '', label: 'Vazio' },
          { value: 'Em prospecção', label: 'Em prospecção' },
          { value: 'Em negociação', label: 'Em negociação' },
          { value: 'Em setup', label: 'Em setup' },
          { value: 'Parceiras', label: 'Parceiras' },
          { value: 'Inativo', label: 'Inativo' },
        ];
      case 'distributors':
        return distributors;
      default:
        return [];
    }
  };

  // Se disabled, não permitir edição
  if (disabled) {
    return (
      <div className="flex items-center justify-between px-2 py-1">
        <span className="text-gray-500 font-medium truncate flex-1">
          {getValueDisplay()}
        </span>
        {onShowHistory && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onShowHistory();
            }}
            className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors ml-2"
            title="Ver histórico"
          >
            <Clock className="h-3 w-3" />
          </button>
        )}
      </div>
    );
  }

  if (isEditing) {
    const inputType = getInputType();

    return (
      <div className="flex items-center space-x-2 min-w-0">
        {type === 'distributors' || type === 'categoria_mercado' ? (
       <div className="flex-1 min-w-0" style={{ minWidth: "300px" }}>
         <Select
           isMulti
           
           options={getSelectOptions()}
           value={(editValue ? editValue.split(',').filter(Boolean) : [])
             .map((id) => {
               const opt = getSelectOptions().find((o: any) => String(o.value) === String(id));
               return opt ?? { value: id, label: id };
             })}
           onChange={(opts) => {
             const arr = Array.isArray(opts) ? opts.map((o: any) => o.value) : [];
             setEditValue(arr.join(','));
           }}
           
           isDisabled={isSaving}
           placeholder="Selecione distribuidores..."
           closeMenuOnSelect={false}
           classNamePrefix="rs"
           menuPlacement="auto"
           // Mantém o menu acima de tabelas com overflow
           menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
           styles={{
             menuPortal: (base) => ({ ...base, zIndex: 9999 }),
           }}
         />
       </div>
     ) : inputType === 'select' ? (
          <select
            value={editValue}
            
            onChange={(e) => {
              
                setEditValue((e.target as HTMLSelectElement).value);
              
            }}
            onKeyDown={handleKeyDown}
            className="flex-1 px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-0 bg-white"
            autoFocus
            disabled={isSaving}
          >
            {getSelectOptions().map((option:any) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : inputType === 'date' ? (
          <input
            type="date"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-0"
            autoFocus
            disabled={isSaving}
          />
        ) : inputType === 'number' ? (
          <input
            type="number"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-0"
            autoFocus
            disabled={isSaving}
            step={type === 'percent' ? '0.01' : undefined}
            placeholder={type === 'percent' ? 'Ex: 15.50' : undefined}
          />
        ) : (
          <input
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-0"
            autoFocus
            disabled={isSaving}
          />
        )}
        <div className="flex space-x-1">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="p-1 text-green-600 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
            title="Salvar"
          >
            <Check className="h-4 w-4" />
          </button>
          <button
            onClick={handleCancel}
            disabled={isSaving}
            className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
            title="Cancelar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="group flex items-center justify-between hover:bg-gray-50 px-2 py-1 rounded transition-colors"
    >
      <span 
        className="font-medium truncate cursor-pointer flex-1"
        onClick={() => setIsEditing(true)}
      >
        {getValueDisplay()}
      </span>
      <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity ml-2">
        {onShowHistory && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onShowHistory();
            }}
            className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
            title="Ver histórico"
          >
            <Clock className="h-3 w-3" />
          </button>
        )}
        <Edit3 className="h-3 w-3 text-gray-400 flex-shrink-0" />
      </div>
    </div>
  );
};